import { useQuery } from "react-query";
import i18next from "i18next";

import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { zodParseWithDetailedLog } from "src/utils/zodErrorLogger";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { AiCommentInput, AiCommentOutputSchema, type AiCommentOutput } from "./aiCommentSchema";

export const generateAiCommentKey = "generateAiComment" as const;

export type GenerateAiCommentRequest = {
    editorialPrompt?: string;
    parameterKeys: string[];
    templateId?: string;
    ssqVersionId?: number;
    entityType?: string;
    entityId?: number;
};

const postAiComment = async (
    request: GenerateAiCommentRequest,
    organizationId: number,
    campaignId: number | undefined,
): Promise<AiCommentOutput> => {
    const payload: AiCommentInput = {
        templateId: request.templateId ?? "cms_narrative",
        editorialPrompt: request.editorialPrompt ?? null,
        parameterKeys: request.parameterKeys,
        locale: i18next.resolvedLanguage || i18next.language || "en",
        campaignId: campaignId ?? null,
        ssqVersionId: request.ssqVersionId ?? null,
        entityType: request.entityType ?? null,
        entityId: request.entityId ?? null,
    };

    const response = await loggedInApiCall.post("/api/ai-comments", payload, {
        params: {
            organization_id: organizationId,
        },
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
    });

    return zodParseWithDetailedLog(response.data, AiCommentOutputSchema, {
        operation: generateAiCommentKey,
    });
};

export const useGenerateAiComment = (request: GenerateAiCommentRequest | null, enabled: boolean) => {
    const { selectedCompany, currentCampaign } = useFoodpilotContext();
    const organizationId = selectedCompany?.id;

    return useQuery({
        queryKey: [
            generateAiCommentKey,
            organizationId,
            currentCampaign?.id,
            request?.editorialPrompt,
            request?.parameterKeys,
            request?.ssqVersionId,
            request?.entityType,
            request?.entityId,
        ],
        enabled: enabled && request !== null && typeof organizationId === "number",
        staleTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false,
        queryFn: async () => {
            if (request === null || organizationId === undefined) {
                throw new Error("Organization context is required to generate AI comments.");
            }

            return postAiComment(request, organizationId, currentCampaign?.id);
        },
    });
};
